/* eslint-disable react/no-array-index-key */

import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as CheckoutContext } from '../../../context/checkout';
import { Context as RestaurantContext } from '../../../context/restaurant';
import SplitBillModal from './splitBill/modal';
import OrderProduct from './orderProduct';
import { useCurrency } from '../../../hooks/useCurrency';
import MainButton from '../../../components/buttons/main';
import { PriceUnderline, PriceCrossed } from '../../../components/svg/textDecorations';
import OrderPayments from './orderPayments';
import OrdersNavigation from './ordersNavigation';
import { useBackPress } from '../../../hooks/useBackPress';

const OrderBill = ({ orders, table }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();

  const { setFullBill, setSplitBill } = useContext(CheckoutContext);
  const { state: restaurantState, setActiveOrder } = useContext(RestaurantContext);

  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(
    !orders || orders.length === 0
      ? null
      : (
          orders.find(
            (x) => !!restaurantState.activeOrder && x.id === restaurantState.activeOrder,
          ) ||
          orders[0] ||
          {}
        ).id,
  );

  const { handleBackOnModalOpen, handleBackOnModalClose } = useBackPress('/bill', () =>
    setSplitModalOpen(false),
  );

  if (!selectedOrderId) {
    return null;
  }

  const selectedOrder = orders.find((x) => x.id === selectedOrderId);
  const hasPayments = selectedOrder && selectedOrder.payments && selectedOrder.payments.length > 0;
  const isAllPaid = (selectedOrder.price || {}).leftToPay <= 0;

  return (
    <>
      <div>
        {orders && orders.length > 1 && (
          <OrdersNavigation
            orders={orders}
            selected={selectedOrder.id}
            onSelect={(val) => {
              setActiveOrder(val.id);
              setSelectedOrderId(val.id);
            }}
          />
        )}
        <div className="max-w-lg mx-auto">
          <div className="flex flex-row justify-between items-center">
            <p
              className={`font-primaryMedium ${hasPayments ? 'text-lg' : 'text-2xl'} text-primary`}
            >
              {t('order.tableBill')}
            </p>
            <p
              className={`font-primaryMedium ${
                hasPayments ? 'text-lg' : 'text-2xl'
              } text-primary whitespace-nowrap`}
            >
              <span className="relative">
                {(selectedOrder.price || {}).total.toFixed(2)} {currencySymbol}
                {!hasPayments && <PriceUnderline />}
                {hasPayments && <PriceCrossed />}
              </span>
            </p>
          </div>
          {hasPayments && (
            <div className="flex flex-row justify-between items-center mt-2">
              <p className="font-primaryMedium text-2xl text-primary">{t('order.leftToPay')}</p>
              <p className="font-primaryMedium text-2xl text-primary whitespace-nowrap">
                <span className="relative">
                  {(selectedOrder.price || {}).leftToPay.toFixed(2)} {currencySymbol}
                  <PriceUnderline />
                </span>
              </p>
            </div>
          )}
          {hasPayments && <OrderPayments payments={selectedOrder.payments} />}
          <div className="mt-7" />
          {(selectedOrder.products || []).map((product, ind) => {
            return <OrderProduct key={`product.id-${ind}`} product={product} />;
          })}
        </div>
        {!isAllPaid && (
          <div className="mt-10 mb-3 max-w-xs mx-auto">
            <MainButton
              text={t('buttons.payFullBill')}
              onClick={() => {
                setFullBill({
                  subtotal: hasPayments
                    ? (selectedOrder.price || {}).leftToPay
                    : (selectedOrder.price || {}).total,
                  orderId: selectedOrder.id,
                });
                navigate('/checkout');
              }}
            />
            <div className="mt-3" />
            <MainButton
              text={t('buttons.splitBill')}
              outline
              onClick={() => {
                handleBackOnModalOpen();
                setSplitModalOpen(true);
              }}
            />
          </div>
        )}
      </div>
      <SplitBillModal
        order={selectedOrder}
        table={table}
        open={splitModalOpen}
        onToggle={(val) => {
          setSplitModalOpen(val);

          if (!val) {
            handleBackOnModalClose();
          }
        }}
        onConfirm={(splitType, data) => {
          setSplitBill({ splitType, data });
          navigate('/checkout', { replace: true });
        }}
      />
    </>
  );
};

export default OrderBill;
